import { Key } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Field, Steps, Callout, RoleBadge, pathFor,
  Diagram, MockField, MockSelect, MockButton, MockTable, MockRow,
} from "../shared";

export default function SettingsApiKeysSection() {
  return (
    <div data-testid="guide-settings-api-keys">
      <PageHeading
        icon={Key}
        title="Agent API Keys (Jeeves)"
        description="Create and cancel the 'passes' that let outside AI helpers and other tools connect to the system. This page is the IT view — for the full developer details, see Agent API (Jeeves)."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="it_admin" />
        <span className="text-xs text-white/40">Needs the <span className="font-mono text-white/70">manage_api_keys</span> permission. Creating and cancelling both ask for your password first.</span>
      </div>

      <Callout variant="info" title="In plain words">
        An API key is like a special password you hand to an outside tool so it can talk to the system on your behalf. You can decide exactly what each key is allowed to do, and you can cancel it at any time.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          When an outside tool (an AI assistant, a chatbot, a sync robot) needs to read or update tasks. You make a key for it, hand the key over securely, and cancel it the moment you no longer trust it.
        </p>
      </Section>

      <Section title="The screen">
        <Diagram
          frameLabel="Settings → Agent API Keys"
          legend={[
            <>Give the key a label so you remember what it's for.</>,
            <>Choose what the key is allowed to do (its scopes).</>,
            <>Press <span className="text-white">Generate Key</span> and confirm with your password.</>,
            <>To stop a key, press <span className="text-white">Revoke</span> on its row.</>,
          ]}
        >
          <MockField label="Label" value="Slack assistant" marker={1} />
          <MockSelect label="Allowed scopes" value="tasks:read, tasks:write" marker={2} />
          <MockButton label="Generate Key" marker={3} />
          <MockTable
            columns={["Label", "Scopes", "Last used", ""]}
            rows={[
              ["Slack assistant", "tasks:read", "2 min ago", <MockButton label="Revoke" marker={4} variant="danger" />],
            ]}
          />
        </Diagram>
      </Section>

      <Section title="Each field explained">
        <div className="space-y-2">
          <Field name="Label" required="required"><p>A free-text name so you remember what the key is for (e.g. "Slack assistant", "Jira sync bot").</p></Field>
          <Field name="Allowed scopes" required="required"><p>The list of things the key may do — chosen from <span className="text-white">tasks:read</span>, <span className="text-white">tasks:write</span>, <span className="text-white">tasks:manage</span>, <span className="text-white">subtasks:write</span>, <span className="text-white">people:read</span>, <span className="text-white">lanes:read</span>, <span className="text-white">summary:read</span>. Use <span className="text-white">*</span> for full access (rarely needed).</p></Field>
          <Field name="Act as" required="optional"><p>The username the key acts as when it makes changes. This is who shows up in the history trail for its actions.</p></Field>
          <Field name="Expires" required="optional"><p>An optional end date. After it passes, the key stops working even if you never cancel it.</p></Field>
        </div>
      </Section>

      <Section title="How to create a key">
        <Steps steps={[
          <>Press <span className="text-white">Generate Key</span> and fill in the label, scopes, and the optional act-as and expiry.</>,
          <>Confirm with your password.</>,
          <>The new key is shown <span className="text-white">once</span>, in the form <span className="font-mono text-white">keyId.secret</span>. Copy it right away — the secret half cannot be shown again. After you close the box, only the label, scopes, and last-used time stay visible.</>,
          <>Hand the key to the tool's owner over a secure channel.</>,
        ]} />
      </Section>

      <Section title="How to cancel a key">
        <Steps steps={[
          <>Find the active key in the list.</>,
          <>Press <span className="text-white">Revoke</span> and confirm with your password.</>,
          <>The very next request from that key is refused with <span className="text-white">401 Invalid or revoked API key</span>. Cancelling takes effect immediately.</>,
        ]} />
      </Section>

      <Callout variant="warning" title="Limits and tracking">
        Every request from a key is recorded (and failures go into the System Diagnostics Log). Each key may make up to <span className="text-white">120 requests per minute</span>. Failed logins don't count against that limit but are still recorded.
      </Callout>
      <Callout variant="info" title="Looking for the developer details?">
        See <Link href={pathFor("agent-api")} className="text-cyan-300 hover:underline">Agent API (Jeeves)</Link> for the addresses, scopes, and example requests an integrator needs.
      </Callout>
    </div>
  );
}
